import React from 'react';
import cn from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import styles from './Nav.module.scss';
import Image from '../../Image';
import logo from '../../../assets/logo.jpg';
import cart from '../../../assets/nav/cart.png';
import menu from '../../../assets/nav/menu.png';
import { routes } from '../../../routes';
import useBreakpoints from '../../../hooks/use-breakpoints';
import MobileNav from './MobileNav';
import useNumCartItems from '../../../hooks/useNumCartItems';
import { useCustomer } from '../../../contexts/CustomerContext';

export default function Nav() {
  const { pathname } = useLocation();
  const breakpoint = useBreakpoints();
  const { customer } = useCustomer();

  const getCurrentDomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('shoob.cloud')) {
      return 'https://www.shoob.cloud';
    } else {
      return 'https://www.shoobphoto.com';
    }
  }

  const { data } = useNumCartItems({ id: customer?.cart?.id, email: customer?.cart?.email || customer?.email });

  const isSignedIn = document.querySelector('#current-user');

  if (['lg', 'md', 'sm'].includes(breakpoint)) return <MobileNav />;

  return (
    <nav
      className={cn({
        [styles.navContainer]: true,
        [styles.customerNav]: true,
        [styles.pb]: pathname !== '/',
      })}
    >
      <div className={styles.navLeft}>
        <div className={cn(styles.cartLink, styles.dropdown, styles.hoverDropdown)} style={{ marginLeft: '0.5em' }}>
          <button
            className={styles.button}
            style={{ padding: '.5em' }}
            type="button"
          >
            <Image alt="Menu" src={menu} />
          </button>
          <ul className={cn(styles.dropdownMenu, styles.hoverDropdownMenu)}>
            <li>
              <a href={isSignedIn ? '/pages/home' : '/users/sign_in'}>
                <svg className={styles.loginItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 21V10l8-5 8 5v11" />
                  <rect x="9" y="14" width="6" height="7" />
                  <path d="M12 5V3" />
                </svg>
                {isSignedIn ? 'Home Page' : 'School Login'}
              </a>
            </li>
            {!isSignedIn && (
              <li>
                <a href="/users/sign_in">
                  <svg className={styles.loginItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21v-2a8 8 0 0116 0v2" />
                  </svg>
                  Shoob Employee Login
                </a>
              </li>
            )}
          </ul>
        </div>
        <Link to="/">
          <Image src={logo} classes={styles.logo} />
        </Link>
      </div>
      <ul className={styles.nav}>
        <li className={cn(styles.dropdown, styles.hoverDropdown)}>
          <a href="/seniors">
            Senior Portraits
          </a>
          <ul className={cn(styles.dropdownMenu, styles.hoverDropdownMenu)}>
            <li>
              <a href="/seniors">About Senior Portraits</a>
            </li>
            <li>
              <Link to={routes.scheduleAppointment({ senior: true })}>
                Book Senior Appointment
              </Link>
            </li>
            <li>
              <Link to={routes.scheduleAppointment({ senior: true })}>
                Upgrade My Session
              </Link>
            </li>
            <li>
              <Link to={routes.studentIdEntry({ order: 'senior' })}>
                Order Senior Portraits
              </Link>
            </li>
            <li>
              <a href="/seniors#faq">Seniors FAQ</a>
            </li>
          </ul>
        </li>

        <li className={cn(styles.dropdown, styles.hoverDropdown)}>
          <button className={styles.button} type="button">
            Order
          </button>
          <ul className={cn(styles.dropdownMenu, styles.hoverDropdownMenu)}>
            <li>
              <Link to={routes.studentIdEntry()}>
                School Pictures
              </Link>
            </li>
            <li>
              <Link to={routes.studentIdEntry({ order: 'senior' })}>
                Senior Portraits
              </Link>
            </li>
            <li>
              <Link to={routes.studentIdEntry({ order: 'yearbook_order' })}>
                Yearbooks
              </Link>
            </li>
            <li>
              <Link to={routes.studentIdEntry({ order: 'panorama' })}>
                Groups and Panorama
              </Link>
            </li>
          </ul>
        </li>

        <li className={cn(styles.dropdown, styles.hoverDropdown)}>
          <button className={styles.button} type="button">
            Book Appointment
          </button>
          <ul className={cn(styles.dropdownMenu, styles.hoverDropdownMenu)}>
            <li>
              {/* `senior: true` makes the schedule-appointment page resolve the
                  current senior-sessions pricelist by season server-side
                  (Package.current_senior_sessions) — no package id is pinned
                  here, so the link never rots into last season's package. */}
              <Link to={routes.scheduleAppointment({ senior: true })}>
                Book High School Senior Portrait Appointment
              </Link>
            </li>
            <li>
              <Link to={routes.scheduleAppointment({ senior: false })}>
                Book Elementary School Appointment
              </Link>
            </li>
            <li>
              <Link to={routes.rescheduleAppointment()}>
                Reschedule Appointment
              </Link>
            </li>
          </ul>
        </li>
        <li>
          <Link to={routes.downloadImageForm()}>
            Download Digital Files
          </Link>
        </li>
        <li className={cn(styles.dropdown, styles.hoverDropdown)}>
          <a href="/yearbooks">Yearbooks</a>
          <ul className={cn(styles.dropdownMenu, styles.hoverDropdownMenu)}>
            <li>
              <a href="/yearbooks">About Yearbooks</a>
            </li>
            <li>
              <a href="/yearbook_requests/new">Sign Up</a>
            </li>
            <li>
              <a href="/yearbook_requests/pricing_calculator">Pricing Calculator</a>
            </li>
          </ul>
        </li>
        <li>
          <a href="/school_resources">
            Resources for Schools
          </a>
        </li>
      </ul>
      <div className={styles.navRight}>
        <ul className={styles.utilityNav}>
          <li className={styles.secondaryItem}>
            <a href={getCurrentDomain() === 'https://www.shoob.cloud' ? 'https://www.shoobphotosouth.com/contact1' : '/contacts/new'}>
              <svg className={styles.secondaryItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.79.34 1.57.63 2.32a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.76-1.76a2 2 0 012.11-.45c.75.29 1.53.5 2.32.63A2 2 0 0122 16.92z" />
              </svg>
              Contact Us
            </a>
          </li>
          <li className={styles.secondaryItem}>
            <Link to={routes.registration()}>
              <svg className={styles.secondaryItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
              Register
            </Link>
          </li>
          <li className={styles.secondaryItem}>
            <a href='/privacy-policy'>
              <svg className={styles.secondaryItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              Privacy &amp; Data Policy
            </a>
          </li>
          <li className={styles.secondaryItem}>
            <a href='/guarantee'>
              <svg className={styles.secondaryItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M9 12l2 2 4-4" />
              </svg>
              Guarantee
            </a>
          </li>
        </ul>
        <Link className={styles.cartLink} to={routes.cart()} style={{ paddingRight: '1em' }}>
          <div style={{ position: 'relative' }}>
            <Image src={cart} />
            {data > 0 && <span style={{ textAlign: 'center', position: 'absolute', top: '-8px', right: '-10px', background: '#008fcf', color: '#fff', minWidth: '18px', height: '18px', borderRadius: '9px', padding: '0 5px', fontSize: '11px', lineHeight: '18px', fontWeight: 600 }}>{data}</span>}
          </div>
        </Link>
      </div>
    </nav>
  );
}
